import instance from "./axiosInstance";
import { isAgentDraftItem } from "./agentDraft";
import { getScopedReturnMediaIds } from "./returnMedia";
import {
  hasValidEncipherencode,
  sanitizeEncipherencode,
} from "./encipherencode";
import { hasValidTrackingReference } from "./routes";
import { reconcileReturnCartAvailability } from "./returnRequestAvailability";
import { deriveRequestReturnType } from "./returnTypes";

const rejectMissingIdentifier = (message) => Promise.reject(new Error(message));

function getCookie(cookieName) {
  const name = cookieName + "=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(";");

  for (let i = 0; i < cookieArray.length; i++) {
    let cookie = cookieArray[i].trim();
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }

  return null;
}

function normalizeReturnLineItems(lineItems = []) {
  if (!Array.isArray(lineItems)) {
    return [];
  }

  return lineItems.map((item) => ({
    ...item,
    exchange_variant_image_url:
      typeof item.exchange_variant_image_url === "string"
        ? item.exchange_variant_image_url
        : "",
  }));
}

function normalizeRequestTypeAndLineItems(
  returnType,
  returnedProducts,
  isWarrantyClaim = false,
) {
  const requestLineItems = normalizeReturnLineItems(returnedProducts).map(
    (item) =>
      isWarrantyClaim
        ? { ...item, return_type: "warranty_claim" }
        : item,
  );
  const requestReturnType = isWarrantyClaim
    ? "warranty_claim"
    : deriveRequestReturnType(
      requestLineItems.map((item) => item.return_type),
      returnType,
    );

  return {
    requestReturnType,
    requestLineItems,
  };
}

// Merchant and RMS Config
export function configAPI(encipherencode, cname, test_mode) {
  return instance.get(
    `${process.env.REACT_APP_BE_DOMAIN}/api/v2/configurations`,
    {
      params: {
        encipherencode,
        cname,
        test_mode
      },
    }
  );
}

// Send OTP
export function sendOTPRequest(email, encipherencode, phone) {
  if (!hasValidEncipherencode(encipherencode)) {
    return rejectMissingIdentifier("Merchant identity is required");
  }

  const params = {
    encipherencode: sanitizeEncipherencode(encipherencode),
    ...(phone && phone !== null && { phone }),
    ...(email && email !== null && { email }),
  };

  return instance.get(
    `${process.env.REACT_APP_BE_DOMAIN}/api/v2/authenticate/send_otp`,
    {
      params,
    }
  );
}

// OTP Verification
export function verifyOTP(encipherencode, unique_id, otp) {
  if (!hasValidEncipherencode(encipherencode)) {
    return rejectMissingIdentifier("Merchant identity is required");
  }

  return instance.get(
    `${process.env.REACT_APP_BE_DOMAIN}/api/v2/authenticate/verify_otp?encipherencode=${sanitizeEncipherencode(encipherencode)}&unique_id=${unique_id}&otp=${otp}`
  );
}

// Verify Signed URL
export function verifySignedUrl(encipherencode, staff_email, p, e, signed_key) {
  const params = {
    signed_key,
    staff_email,
    encipherencode,
    ...(p && p !== null && { phone: p }),
    ...(e && e !== null && { email: e }),
  };

  return instance.get(`${process.env.REACT_APP_API_URL}/verify_signed_url`, {
    params,
  });
}

// Orders List
export function ordersList(offset, order_name, order_id) {
  return instance.get(`${process.env.REACT_APP_BE_DOMAIN}/api/v2/orders`, {
    params: {
      ...(order_name && { order_name }),
      ...(order_id && { order_id }),
      offset,
      limit: 50,
    },
  });
}

// Return Requests List
export function returnRequestsAPI(offSet) {
  return instance.get(`${process.env.REACT_APP_BE_DOMAIN}/api/v2/requests`, {
    params: {
      offset: offSet,
      limit: 5,
    },
  });
}

// Delivery Status Check
export function deliveryStatusCheck(merchant_id, order_id) {
  return instance.get(`${process.env.REACT_APP_API_URL}/orders/order-status`, {
    params: {
      merchant_id,
      order_id,
    },
  });
}

// Pincode Serviceabilty Check
export function serviceabiltyCheck(pincode, merchant_id) {
  return instance.get(
    `${process.env.REACT_APP_API_URL}/rms/check/serviceability`,
    {
      params: {
        merchant_id: merchant_id,
        pincode: pincode,
      },
    }
  );
}

// Selected Order Details
export function selectedOrderAPI(selectedOrder) {
  return instance.post(
    `${process.env.REACT_APP_BE_DOMAIN}/api/v2/orders`,
    selectedOrder
  );
}

export async function validateReturnCartAvailability(
  selectedOrder,
  cartItems,
) {
  const response = await selectedOrderAPI(selectedOrder);
  const freshOrder = response?.data?.order;
  if (!freshOrder || !Array.isArray(freshOrder.order_items)) {
    throw new Error(
      "We could not verify the latest order availability. Please try again.",
    );
  }

  return {
    freshOrder,
    uniqueId: response?.data?.unique_id,
    ...reconcileReturnCartAvailability(cartItems, freshOrder),
  };
}

// Selected Return Request Details
export function selectedReturnOrderAPI(
  merchant_id,
  id,
  reference_id,
  create_exchange_order
) {
  const validId = hasValidTrackingReference(id) ? id : null;
  const validReferenceId = hasValidTrackingReference(reference_id)
    ? String(reference_id).trim()
    : null;

  if (!validId && !validReferenceId) {
    return rejectMissingIdentifier("Return request identifier is required");
  }

  let params = {
    merchant_id: merchant_id,
    create_exchange_order: create_exchange_order,
  };
  if (validId) {
    params.id = validId;
  }
  if (validReferenceId) {
    params.reference_id = validReferenceId;
  }
  return instance.get(`${process.env.REACT_APP_API_URL}/rms/return/request`, {
    params,
  });
}

export function selectedReturnOrderAPIV2(
  merchant_id,
  id,
  reference_id
) {
  const validId = hasValidTrackingReference(id) ? id : null;
  const validReferenceId = hasValidTrackingReference(reference_id)
    ? String(reference_id).trim()
    : null;

  if (!validId && !validReferenceId) {
    return rejectMissingIdentifier("Return request identifier is required");
  }

  let params = {
    merchant_id,
  };
  if (validId) {
    params.id = validId;
  }
  if (validReferenceId) {
    params.reference_id = validReferenceId;
  }
  return instance.get(`${process.env.REACT_APP_BE_DOMAIN}/api/v2/request_detail`, {
    params,
  });
}

// Return Reasons
export function getReasons(return_type, product_id, extraParams = {}) {
  return instance.get(`${process.env.REACT_APP_BE_DOMAIN}/api/v2/reasons`, {
    params: {
      return_type,
      product_id,
      ...extraParams,
    },
  });
}

export function getReasonFirstReasons({ order_id, source_line_item_id } = {}) {
  const params = {};
  if (order_id !== undefined && order_id !== null && order_id !== "") {
    params.order_id = order_id;
  }
  if (
    source_line_item_id !== undefined &&
    source_line_item_id !== null &&
    source_line_item_id !== ""
  ) {
    params.source_line_item_id = source_line_item_id;
  }
  const url = `${process.env.REACT_APP_BE_DOMAIN}/api/v2/reason-first-reasons`;
  return Object.keys(params).length > 0
    ? instance.get(url, { params })
    : instance.get(url);
}

// Same Variant Products
export function getSameVariants(
  merchant_id,
  product_id,
  compare_inventory_qty,
  compare_variant_price,
  variant_id,
  variant_sku,
  lower_value,
  higher_value,
  api_version
) {
  let domain = process.env.REACT_APP_API_URL;
  if (api_version === "v2") {
    domain = domain.replace("/v1", `/${api_version}`);
  }
  return instance.get(`${domain}/products/product_variants`, {
    params: {
      merchant_id,
      product_id: String(product_id),
      compare_inventory_qty,
      compare_variant_price,
      variant_id,
      variant_sku,
      lower_value:
        lower_value === undefined ? undefined : lower_value ? "yes" : "no",
      higher_value:
        higher_value === undefined ? undefined : higher_value ? "yes" : "no",
    },
  });
}

// Different Variant Products
export function getDifferentVariants(
  merchant_id,
  product_id,
  compare_inventory_qty,
  compare_variant_price,
  offset,
  collection_id,
  query,
  sku_query,
  lower_value,
  higher_value,
  variant_id,
  variant_sku,
  api_version
) {
  let domain = process.env.REACT_APP_API_URL;
  if (api_version === "v2") {
    domain = domain.replace("/v1", `/${api_version}`);
  }
  return instance.get(`${domain}/products/products`, {
    params: {
      merchant_id,
      product_id: String(product_id),
      compare_inventory_qty,
      compare_variant_price,
      limit: 12,
      offset,
      collection_id,
      query,
      sku_query,
      variant_id,
      variant_sku,
      lower_value:
        lower_value === undefined ? undefined : lower_value ? "yes" : "no",
      higher_value:
        higher_value === undefined ? undefined : higher_value ? "yes" : "no",
    },
  });
}

// Different Product Collections
export function getCollections(merchant_id) {
  return instance.get(`${process.env.REACT_APP_API_URL}/products/collections`, {
    params: {
      merchant_id,
    },
  });
}

// Filters
export function getAttributes(merchant_id, api_version) {
  let domain = process.env.REACT_APP_API_URL;
  domain = domain.replace("/v1", `/${api_version}`);
  return instance.get(`${domain}/products/attributes/all`, {
    params: {
      merchant_id,
    },
  });
}

// Filtered Products
export function getAttributeProducts(merchant_id, api_version, attributes) {
  let domain = process.env.REACT_APP_API_URL;
  domain = domain.replace("/v1", `/${api_version}`);
  return instance.post(`${domain}/products/attributes/products`, {
    merchant_id,
    attributes,
  });
}

// Custom Size Variants Flo
export function customSizeVariantApi(
  merchant_id,
  product_id,
  variant_id,
  length,
  breadth
) {
  return instance.get(
    `${process.env.REACT_APP_API_URL}/flo_mattress/custom_size_variant?merchant_id=${merchant_id}&product_id=${product_id}&variant_id=${variant_id}&length=${length}&breadth=${breadth}`
  );
}

// Create Return Request
export function createRequest(
  merchant_id,
  encipherencode,
  selectedItem,
  shipmentMethod,
  returnedProducts,
  rmsConfig,
  addressDetails,
  upload_media,
  return_type,
  refundSource,
  refund_json,
  userEmail,
  userPhoneNumber,
  firstName,
  lastName,
  reference_id,
  uploaded_media_ids,
  refund_charges,
  exchange_charges,
  shipping_charges,
  unique_id,
  offer_refund_mode,
  offer_refund_mode_on,
  refund_mode_offer_amount,
  button_version,
  discount_config,
  total_exchange_charges,
  total_refund_charges,
  amount,
  customer_id,
  payment_method,
  frontend_version,
  isWarrantyClaim = false,
  warranty_charges = 0,
  pricing_breakdown = null,
) {
  if ((returnedProducts || []).some(isAgentDraftItem)) {
    return Promise.reject(new Error("Connect to Agent selections must be saved as a separate draft."));
  }
  const updated_upload_media_ids = getScopedReturnMediaIds(
    uploaded_media_ids,
    returnedProducts,
  );
  const { requestReturnType, requestLineItems } =
    normalizeRequestTypeAndLineItems(
      return_type,
      returnedProducts,
      isWarrantyClaim,
    );
  const hasReasonFirstRefundItems = requestLineItems.some(
    (item) => item.return_type === "refund" && item.reason_mode === "reason_first",
  );
  const hasLegacyRefundItems = requestLineItems.some(
    (item) => item.return_type === "refund" && item.reason_mode !== "reason_first",
  );

  const body = {
    merchant_id,
    encipherencode,
    order_id: selectedItem[0].order_id,
    order_number: selectedItem[0].order_number,
    order_name: selectedItem[0].order_name,
    customer_preferred_return_shipment_method: shipmentMethod,
    return_type: requestReturnType,
    customer_preferred_refund_method:
      requestReturnType === "warranty_claim" || (hasReasonFirstRefundItems && !hasLegacyRefundItems)
        ? null
        : refundSource,
    customer_first_name: firstName,
    customer_last_name: lastName,
    customer_email: userEmail,
    customer_phone_number: userPhoneNumber,
    customer_address_line_1: addressDetails.addressLine1,
    customer_address_line_2: addressDetails.addressLine2,
    customer_address_city: addressDetails.city,
    customer_address_pin: addressDetails.pincode,
    customer_address_state: addressDetails.state,
    customer_address_country:
      addressDetails.country ||
      selectedItem[0].shipping_address?.country ||
      selectedItem[0].billing_address?.country,
    product_tag_exists: rmsConfig.product_tag_consent,
    line_items: requestLineItems,
    bank_transfer_charge: 0,
    coupon_charge: 0,
    exchange_shipment_charge: 0,
    return_shipment_charge: 0,
    reimburse_amount: 0,
    refund_json,
    reference_id,
    uploaded_media_ids: updated_upload_media_ids,
    ga_client_id: getCookie("_ga"),
    refund_charges,
    exchange_charges,
    shipping_charges,
    warranty_charges,
    unique_id,
    offer_refund_mode,
    offer_refund_mode_on,
    refund_mode_offer_amount,
    button_version,
    discount_config,
    total_exchange_charges,
    total_refund_charges,
    amount,
    customer_id,
    payment_method,
    pricing_breakdown,
  };
  // return instance.post(
  //   `${process.env.REACT_APP_API_URL}/rms/create/return_request`,
  //   body,
  //   {
  //     params: {
  //       upload_media,
  //     },
  //   }
  // );
  return instance.post(
    `${process.env.REACT_APP_BE_DOMAIN}/api/v2/create`,
    body,
    {
      params: {
        upload_media,
      },
    }
  );
}

// Validate Media Type
export function validateMedia(
  files,
  merchant_id,
  check_reasons,
  reason_id,
  reason_code,
  reason_type,
  order_id,
  order_number
) {
  if (files && merchant_id) {
    return instance.post(
      `${process.env.REACT_APP_API_URL}/rms/create/return_request/validate_media`,
      files,
      {
        params: {
          merchant_id,
          reason_id,
          reason_code,
          reason_type,
          check_reasons,
          order_id,
          order_number,
        },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  }
}

// Validate Media Type
export function getS3SignedUrls(files, order_id, order_number, line_item_id) {
  return instance.post(
    `${process.env.REACT_APP_BE_DOMAIN}/api/v2/media_signed_urls`,
    { files, order_id, order_number, line_item_id }
  );
}

export function resubmitReturnImageQC(payload) {
  return instance.post(
    `${process.env.REACT_APP_BE_DOMAIN}/api/v2/return_image_qc/resubmit`,
    payload
  );
}

// Comments list
export function getComments(return_request_id) {
  return instance.get(
    `${process.env.REACT_APP_API_URL}/rms/return_request/comments`,
    {
      params: {
        return_request_id,
      },
    }
  );
}

// Create Comment
export function createComment(return_request_id, comment, upload_media) {
  const body = {
    return_request_id,
    commented_by: "customer",
    comment,
  };
  return instance.post(
    `${process.env.REACT_APP_API_URL}/rms/return_request/comment/create`,
    body,
    {
      params: {
        upload_media,
      },
    }
  );
}

// Comment Media Upload
export function uploadCommentMedia(return_request_id, comment_id, files) {
  return instance.post(
    `${process.env.REACT_APP_API_URL}/rms/create/return_request/comment/upload_media`,
    files,
    {
      params: {
        return_request_id,
        comment_id,
      },
    }
  );
}

// Update Shipping Details
export function updateShippingDetails(file) {
  return instance.post(
    `${process.env.REACT_APP_BE_DOMAIN}/api/v2/self_ship`,
    file
  );
}

// Check for amount to be paid and payment method to be used
export function paymentValidate(
  merchant_id,
  order_number,
  order_id,
  is_cod,
  total_order_price,
  return_type,
  shipmentMethod,
  returnedProducts,
  refundSource,
  preapplied_exchange_charge,
  preapplied_refund_charge,
  isWarrantyClaim = false
) {
  if ((returnedProducts || []).some(isAgentDraftItem)) {
    return Promise.reject(new Error("Connect to Agent selections must be saved as a separate draft."));
  }
  const { requestReturnType, requestLineItems } =
    normalizeRequestTypeAndLineItems(
      return_type,
      returnedProducts,
      isWarrantyClaim,
    );
  const hasReasonFirstRefundItems = requestLineItems.some(
    (item) => item.return_type === "refund" && item.reason_mode === "reason_first",
  );
  const hasLegacyRefundItems = requestLineItems.some(
    (item) => item.return_type === "refund" && item.reason_mode !== "reason_first",
  );

  const body = {
    merchant_id,
    order_number,
    order_id,
    is_cod,
    total_order_price,
    return_type: requestReturnType,
    line_items: requestLineItems,
    customer_preferred_return_shipment_method: shipmentMethod,
    customer_preferred_refund_method:
      hasReasonFirstRefundItems && !hasLegacyRefundItems ? null : refundSource,
    preapplied_exchange_charge,
    preapplied_refund_charge,
  };
  return instance.post(
    `${process.env.REACT_APP_API_URL}/rms/return_request/payment/validate`,
    body
  );
}

// Prepaid payment method api
export function createPayment(
  encipherencode,
  merchant_id,
  customer_first_name,
  customer_last_name,
  amount,
  email,
  phonenumber,
  order_id,
  order_number,
  return_type,
  selectedItem,
  shipmentMethod,
  returnedProducts,
  rmsConfig,
  addressDetails,
  refundSource,
  refund_json,
  userEmail,
  userPhoneNumber,
  reference_id,
  uploaded_media_ids,
  customer_id,
  refund_charges,
  exchange_charges,
  shipping_charges,
  unique_id,
  button_version,
  total_exchange_charges,
  total_refund_charges,
  discount_config,
  pricing_breakdown = null,
) {
  if ((returnedProducts || []).some(isAgentDraftItem)) {
    return Promise.reject(new Error("Connect to Agent selections must be saved as a separate draft."));
  }
  const updated_upload_media_ids = getScopedReturnMediaIds(
    uploaded_media_ids,
    returnedProducts,
  );
  const requestLineItems = normalizeReturnLineItems(returnedProducts);
  const hasReasonFirstRefundItems = requestLineItems.some(
    (item) => item.return_type === "refund" && item.reason_mode === "reason_first",
  );
  const hasLegacyRefundItems = requestLineItems.some(
    (item) => item.return_type === "refund" && item.reason_mode !== "reason_first",
  );
  const body = {
    paymentData: {
      encipherencode,
      merchant_id,
      customer_first_name,
      customer_last_name,
      amount,
      customer_email: email,
      customer_phone_number: phonenumber,
      order_id,
      order_number,
      return_type,
      customer_id,
      total_exchange_charges,
      total_refund_charges,
    },
    requestData: {
      merchant_id,
      order_id: selectedItem[0].order_id,
      order_number: selectedItem[0].order_number,
      order_name: selectedItem[0].order_name,
      return_type,
      customer_preferred_refund_method:
        hasReasonFirstRefundItems && !hasLegacyRefundItems ? null : refundSource,
      customer_preferred_return_shipment_method: shipmentMethod,
      customer_first_name,
      customer_last_name,
      customer_email: userEmail,
      customer_phone_number: userPhoneNumber,
      customer_address_line_1: addressDetails.addressLine1,
      customer_address_line_2: addressDetails.addressLine2,
      customer_address_city: addressDetails.city,
      customer_address_pin: addressDetails.pincode,
      customer_address_state: addressDetails.state,
      customer_address_country:
        addressDetails.country ||
        selectedItem[0].shipping_address?.country ||
        selectedItem[0].billing_address?.country,
      product_tag_exists: rmsConfig.product_tag_consent,
      line_items: requestLineItems,
      bank_transfer_charge: 0,
      coupon_charge: 0,
      exchange_shipment_charge: 0,
      return_shipment_charge: 0,
      reimburse_amount: 0,
      refund_json,
      reference_id,
      uploaded_media_ids: updated_upload_media_ids,
      ga_client_id: getCookie("_ga"),
      refund_charges,
      exchange_charges,
      shipping_charges,
      unique_id,
      button_version,
      discount_config,
      pricing_breakdown,
    },
  };
  return instance.post(
    `${process.env.REACT_APP_API_URL}/rms/return_request/payment/create`,
    body
  );
}

// Check the status of payment
export function paymentStatus(
  merchant_id,
  transaction_id,
  reference_id,
  order_id,
  upload_media,
  create_request
) {
  return instance.get(
    `${process.env.REACT_APP_BE_DOMAIN}/api/v2/payment_status`,
    {
      params: {
        merchant_id,
        transaction_id,
        reference_id,
        order_id,
        upload_media,
        create_request,
      },
    }
  );
}

// Submit Account Details
export function submitDetailsApi(
  merchant_id,
  return_request_id,
  refund_method,
  bank_account_holder_name,
  bank_account_number,
  confirm_bank_account_number,
  bank_ifsc_code,
  vpa_id
) {
  return instance.post(
    `${process.env.REACT_APP_API_URL}/rms/return_request/collect/customer_account_details`,
    {
      merchant_id,
      return_request_id,
      refund_method,
      bank_account_holder_name,
      bank_account_number,
      confirm_bank_account_number,
      bank_ifsc_code,
      vpa_id,
    }
  );
}

export function verifyBankDetailsApi({
  ifsc,
  account_number,
  name,
  phone,
  vpa,
}) {
  return instance.get(
    `${process.env.REACT_APP_BE_DOMAIN}/api/v2/bank_verification`,
    {
      params: {
        ifsc,
        account_number,
        name,
        phone,
        vpa,
      },
    }
  );
}

export function generatePayment(request_id, transaction_id, reference_id) {
  return instance.get(`${process.env.REACT_APP_BE_DOMAIN}/api/v2/generate_payment`, {
    params: {
      request_id,
      transaction_id,
      reference_id,
    }
  })
}

// Mock pickup order details for UI testing
export function fetchPickupOrder(reference_id, awb) {
  return instance.get(`${process.env.REACT_APP_BE_DOMAIN}/api/v2/pickup_image_details`, {
    params: {
      reference_id,
      awb
    }
  })
}

// Pickup image upload - fetch presigned URLs for files
export function getPickupSignedUrls(
  awb,
  files,
  order_id,
  order_number,
  return_request_id,
  line_item_id
) {
  return instance.post(
    `${process.env.REACT_APP_BE_DOMAIN}/api/v2/media_signed_urls`,
    {
      awb,
      files,
      order_id,
      order_number,
      return_request_id,
      line_item_id
    }
  );
}

// Agent drafts have a separate boundary: they never enter operational creation/payment.
export function createAgentDraft(body) {
  return instance.post(`${process.env.REACT_APP_BE_DOMAIN}/api/v2/create-agent-draft`, body);
}
